import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows, FontStacks, Layout } from '../constants/theme';
import { InteractiveButton } from './MicroInteractions';
import PlatformBadge from './PlatformBadge';
import { StandardRefreshButton } from './buttons/RefreshButton';

interface PageHeaderAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'ghost';
  icon?: string;
  disabled?: boolean;
  loading?: boolean;
  ariaLabel?: string;
}

interface UnifiedPageHeaderProps {
  title: string;
  subtitle?: string;
  actions?: PageHeaderAction[];
  metadata?: {
    timestamp?: Date;
    count?: number;
    status?: string;
  };
  showDivider?: boolean;
  showPlatformBadge?: boolean;
  platform?: string;
}

const HeaderContainer = styled.div<{ showDivider?: boolean }>`
  background: ${Colors.surface};
  border-bottom: ${props => props.showDivider ? `1px solid ${Colors.border}` : 'none'};
  box-shadow: ${props => props.showDivider ? Shadows.sm : 'none'};
  position: sticky;
  top: ${Layout.headerHeightPx}; /* Account for main header height */
  z-index: ${Layout.zIndex.content + 5}; /* Lower than filter dropdowns to prevent overlap */
  backdrop-filter: blur(8px);
  background: ${Colors.surfaceGlass};
`;

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${Spacing.xl}px ${Spacing.xl}px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${Spacing.xl}px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${Spacing.lg}px;
    padding: ${Spacing.lg}px ${Spacing.md}px;
  }
`;

const HeaderLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.sm}px;
`;

const PageTitle = styled.h1`
  font-family: ${FontStacks.display};
  font-size: ${FontSizes.display}px;
  font-weight: ${FontWeights.bold};
  color: ${Colors.textPrimary};
  margin: 0;
  letter-spacing: -0.025em;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: ${FontSizes.xxl}px;
  }
`;

const PageSubtitle = styled.p`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.normal};
  color: ${Colors.textSecondary};
  margin: 0;
  line-height: 1.4;
`;

const MetadataContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.lg}px;
  margin-top: ${Spacing.xs}px;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: ${Spacing.md}px;
  }
`;

const MetadataItem = styled.span`
  font-family: ${FontStacks.primary};
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textTertiary};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
`;

const LiveDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #FFC107;
  flex-shrink: 0;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md}px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md}px;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: ${Spacing.sm}px;
  }
`;

const ActionButtonStyled = styled(InteractiveButton)`
  white-space: nowrap;
  min-width: auto;
  
  @media (max-width: 768px) {
    padding: ${Spacing.sm}px ${Spacing.md}px;
    font-size: ${FontSizes.sm}px;
  }
`;

const UnifiedPageHeader: React.FC<UnifiedPageHeaderProps> = ({
  title,
  subtitle,
  actions = [],
  metadata,
  showDivider = true,
  showPlatformBadge = false,
  platform = 'fabric'
}) => {
  const { t, i18n } = useTranslation();

  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffMins < 1) {
      return t('common.justNow', 'Just now');
    } else if (diffMins < 60) {
      return t('common.minutesAgo', { count: diffMins }, `${diffMins}m ago`);
    } else if (diffHours < 24) {
      return t('common.hoursAgo', { count: diffHours }, `${diffHours}h ago`);
    } else {
      return date.toLocaleDateString(i18n.language, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  return (
    <HeaderContainer showDivider={showDivider}>
      <HeaderContent>
        <HeaderLeft>
          <PageTitle>{title}</PageTitle>
          {subtitle && <PageSubtitle>{subtitle}</PageSubtitle>}
          
          {metadata && (
            <MetadataContainer>
              {metadata.timestamp && (
                <MetadataItem>
                  {t('common.lastUpdated', 'Last updated')}: {formatTimestamp(metadata.timestamp)}
                </MetadataItem>
              )}
              {metadata.count !== undefined && (
                <MetadataItem>
                  {metadata.count.toLocaleString()} {t('common.items', 'items')}
                </MetadataItem>
              )}
              {metadata.status && (
                <MetadataItem>
                  {metadata.status === 'Live' && <LiveDot />}
                  {metadata.status}
                </MetadataItem>
              )}
            </MetadataContainer>
          )}
        </HeaderLeft>

        <HeaderRight>
          <ActionsContainer>
            {showPlatformBadge && (
              <PlatformBadge
                currentPlatform={platform}
                showPlatformToggle={false}
                size="sm"
                placement="header"
              />
            )}
            {actions.map((action, index) => {
              // Use StandardRefreshButton for refresh actions
              const isRefreshAction = action.label?.toLowerCase().includes('refresh') || 
                                     action.icon === '🔄';
              
              if (isRefreshAction) {
                return (
                  <StandardRefreshButton
                    key={index}
                    onClick={action.onClick}
                    label={action.label}
                    disabled={action.disabled}
                    ariaLabel={action.ariaLabel}
                  />
                );
              }
              
              // Use regular button for other actions
              return (
                <ActionButtonStyled
                  key={index}
                  variant={action.variant || 'secondary'}
                  size="md"
                  onClick={action.onClick}
                  disabled={action.disabled}
                  loading={action.loading}
                  aria-label={action.ariaLabel || action.label}
                >
                  {action.icon && <span style={{ fontSize: '16px' }}>{action.icon}</span>}
                  {action.label}
                </ActionButtonStyled>
              );
            })}
          </ActionsContainer>
        </HeaderRight>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default UnifiedPageHeader;